import { animate, style, transition, trigger } from '@angular/animations';
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import { OnDestroyComponent } from '@app/shared/components/on-destroy/on-destroy.component';
import { SegmentEvent, User } from '@app/shared/interfaces';
import { CopilotService } from '@app/shared/services/copilot/copilot.service';
import { CountdownService } from '@app/shared/services/countdown/countdown.service';
import { DemoService } from '@app/shared/services/demo/demo.service';
import { InternalCsService } from '@app/shared/services/internal-cs/internal-cs.service';
import { SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { SidebarCountsService } from '@app/shared/services/sidebar-counts/sidebar-counts.service';
import { Team, UserManagementService } from '@app/shared/services/user-management/user-management.service';
import { environment } from '@env/environment';
import { Icon } from '@ui';
import { BehaviorSubject, Observable, combineLatest, fromEvent } from 'rxjs';
import { filter, map, startWith, take, takeUntil, tap } from 'rxjs/operators';
import { CopilotComponent } from '../copilot/copilot/copilot.component';
import { NavbarService } from './navbar.service';
import { NavigationEnd, Router } from '@angular/router';
import { DeviceDetectionService } from '@app/shared/services/device-detection/device-detection.service';

declare let window: any;

export class NavItem {
  icon: Icon;
  griddyIcon?: boolean;
  link?: string;
  external?: boolean;
  tooltipText?: string;
  position: 'top' | 'bottom';
  hidden?: boolean;
  hiddenMobile?: boolean;
  separator?: boolean;
  expandedText: string;
  expandedTooltipText: string;
  class?: string;
  badgeCountKey?: string;
  id?: string;
  comingSoon?: boolean;
  action?: () => void;
}

@Component({
  standalone: false,
  selector: 'sbnb-nav',
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.scss', './nav.component.mobile.scss'],
  host: {
    '(document:click)': 'clickOutside($event)',
    '(document:touchend)': 'clickOutside($event)',
  },
  animations: [
    trigger('enterAnimation', [
      transition(':enter', [style({ opacity: 0 }), animate('150ms', style({ opacity: 1 }))]),
      transition(':leave', [style({ opacity: 1 }), animate('150ms', style({ opacity: 0 }))]),
    ]),
  ],
  providers: [CountdownService],
})
export class NavComponent extends OnDestroyComponent implements OnInit, OnDestroy {
  public navItems: NavItem[];
  public mobileMenuVisible$ = new BehaviorSubject<boolean>(false);
  public currentUser$: Observable<User>;
  public expanded: boolean;
  environment = environment;
  debugMode$ = this.csService.debugMode;
  isCS: boolean;
  badgeCounts: object;
  url = window.location.href;
  overrideExpanded: boolean;
  teams: Team[];
  isTeamOwner = true;
  initialized: boolean;
  animEnabled: boolean;

  perms$ = this.userPermissionsService;

  palantirSrc = '/assets/images/palantir.svg';

  @ViewChild('mainNavigation', { static: false }) mainNavigation: ElementRef;
  @ViewChild('mobileButton') mobileButton: ElementRef;
  @Input('bg-mobile') bgMobile: string;

  public copilotEnabled$ = this.copilotService.enabled$;
  public isSinglePropertyCalendar$ = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    map((event: NavigationEnd) => {
      return event.url.includes('calendar/property');
    }),
    takeUntil(this.destroyed$)
  )

  constructor(
    private authService: AuthenticationService,
    private userPermissionsService: UserPermissionsService,
    private csService: InternalCsService,
    private userManagementService: UserManagementService,
    private sidebarCountsService: SidebarCountsService,
    private navbarService: NavbarService,
    public demoService: DemoService,
    private dialog: MatDialog,
    private copilotService: CopilotService,
    public countdownService: CountdownService,
    private segmentIoService: SegmentIoService,
    private router: Router,
    private deviceDetectionService: DeviceDetectionService,
    private elementRef: ElementRef
  ) {
    super();
  }

  ngOnInit() {

    combineLatest([this.isSinglePropertyCalendar$, this.deviceDetectionService.observeBreakpoint('(min-width: 1245px)')])
      .pipe(
        map(([isSinglePropertyCalendar, sidebarNotCollapsed]) => {
          return isSinglePropertyCalendar && !sidebarNotCollapsed;
        }),
        takeUntil(this.destroyed$)
      ).subscribe((shouldAddAbsoluteClass) => {
        if (shouldAddAbsoluteClass) {
          this.elementRef.nativeElement.classList.add('absolute');
          // acount for open or initial state
          if (this.expanded || this.expanded === undefined) {
            this.expandChange();
          }
        }
        else {
          this.elementRef.nativeElement.classList.remove('absolute');
        }
      });

    this.navbarService.expanded$.subscribe((res) => {
      this.expanded = res;
    });
    this.initialized = true;

    this.currentUser$ = this.authService.user$;

    this.isCS = this.csService.isCS();

    combineLatest([this.currentUser$, this.userPermissionsService.permissions])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([user, permissions]) => {
        if (user) {
          if (permissions.is_team_owner || permissions.is_admin || permissions.all_features) {
            this.navItems = this.getNavItems(user, [], true);
          } else {
            this.navItems = this.getNavItems(user, permissions.features);
          }

          if (this.navItems.find((item) => item.id === 'nav-calendar')) {
            this.userPermissionsService.calendarPermissions$.pipe(take(1), takeUntil(this.destroyed$)).subscribe();
          }
        }
      });

    this.currentUser$.pipe(takeUntil(this.destroyed$)).subscribe((user) => {
      // check for cs user after user change
      this.isCS = this.csService.isCS();

      // check user-related data after user change
      if (user) {
        this.getBadgeCounts();
        this.userManagementService.getTeamsForUser().subscribe((res) => {
          this.teams = res;
          this.isTeamOwner = this.teams.some((team) => team.owner === true);
        });
      }
    });
  }

  initWndowResizeObserver(): Observable<number> {
    return fromEvent(window, 'resize').pipe(
      map((event: any) => (event.target as Window).innerWidth),
      startWith(window.innerWidth)
    );
  }

  checkIfNavbarIsScrollable() {
    if (this.mainNavigation) {
      if (
        this.mainNavigation.nativeElement.scrollHeight > this.mainNavigation.nativeElement.offsetHeight &&
        !this.expanded
      ) {
        this.mainNavigation.nativeElement.style.width = '100px';
      } else {
        this.mainNavigation.nativeElement.style.width = null;
      }
    }
  }

  getBadgeCounts() {
    this.sidebarCountsService.getCountsFromApi().subscribe((res) => {
      this.badgeCounts = res;
    });
  }

  ngOnDestroy() {
    // this.userPermissions.unsubscribe();
  }

  getNavItems(user: User, features = [], all = false) {
    const allNavItems: Record<string, NavItem> = {
      inbox: {
        icon: 'message-simple',
        link: '/inbox',
        expandedText: 'Inbox',
        expandedTooltipText: 'Your unified global inbox \n for guest communication.',
        tooltipText: 'Inbox',
        position: 'top',
        badgeCountKey: 'unread_messages',
        id: 'nav-inbox',
      },
      properties: {
        icon: 'properties-simple',
        link: '/properties',
        expandedText: 'Properties',
        expandedTooltipText: 'Manage your listings and properties.',
        tooltipText: 'Properties',
        position: 'top',
        badgeCountKey: 'new_properties',
        id: 'nav-properties',
      },
      calendar: {
        icon: 'griddy-calendar',
        griddyIcon: true,
        link: '/calendar',
        expandedText: 'Calendar',
        expandedTooltipText: 'Multicalendar for managing prices \n and availability.',
        tooltipText: 'Calendar',
        position: 'top',
        separator: false,
        id: 'nav-calendar',
      },
      gx: {
        icon: 'heart-simple',
        link: '/gx',
        expandedText: 'Guest Experience',
        expandedTooltipText: 'Messaging rules for automating \n guest communication.',
        tooltipText: 'Guest Experience',
        position: 'top',
        id: 'nav-gx',
      },
      ops: {
        icon: 'griddy-checkbox',
        griddyIcon: true,
        link: '/operations',
        expandedText: 'Operations',
        expandedTooltipText: 'Task assignment and team communication.',
        tooltipText: 'Operations',
        position: 'top',
        badgeCountKey: 'unassigned_tasks',
        id: 'nav-ops',
      },
      metrics: {
        icon: 'metrics-simple',
        link: '/metrics',
        expandedText: 'Metrics',
        expandedTooltipText: 'Data driven decisions for your business.',
        tooltipText: 'Metrics',
        position: 'top',
        id: 'nav-metrics',
      },
      connnectedAccounts: {
        icon: 'key-simple',
        link: '/accounts',
        expandedText: 'Connected Accounts',
        expandedTooltipText: 'Connect your Airbnb, Vrbo & Booking.com accounts to Hospitable.',
        tooltipText: 'Connected Accounts',
        position: 'top',
        id: 'nav-connected-accounts',
      },
      direct: {
        icon: 'direct-simple',
        link: '/direct',
        expandedText: 'Direct',
        expandedTooltipText: 'Build your own website tailored to short-term rental bookings.',
        tooltipText: 'Direct',
        position: 'top',
        id: 'nav-direct',
        hidden: false,
      },
      devices: {
        icon: 'griddy-wifi',
        griddyIcon: true,
        link: '/devices',
        expandedText: 'Devices',
        expandedTooltipText: 'Provide guests a seamless experience by using Hospitable to manage your smart devices.',
        tooltipText: 'Devices',
        position: 'top',
        id: 'nav-devices',
        hidden: false,
      },
      copilot: {
        icon: 'magic',
        expandedText: 'Copilot',
        expandedTooltipText: 'Your AI assistant for managing your properties.',
        tooltipText: 'Copilot',
        position: 'bottom',
        id: 'nav-copilot',
        class: 'copilot-nav-item',
        action: () => this.openCopilotModal(),
      },
      community: {
        icon: 'griddy-hands-support',
        expandedText: 'Community',
        expandedTooltipText: 'Connect with other hosts, \n swap tips and ask questions. ',
        tooltipText: 'Community',
        position: 'bottom',
        id: 'nav-community',
        action: () => this.openCommunity(),
      },
      apps: {
        icon: 'apps-simple',
        link: '/apps',
        expandedText: 'Apps',
        expandedTooltipText: 'Expanded functionality through \n integrations, webhooks and API. ',
        tooltipText: 'Apps',
        position: 'bottom',
        id: 'nav-apps',
      },
      help: {
        icon: 'help-simple',
        link: '/support-documentation',
        expandedText: 'Help',
        expandedTooltipText: 'Learn more about Hospitable.',
        tooltipText: 'Support documentation',
        position: 'bottom',
        id: 'nav-help',
      },
    };

    const nav = [];
    const allFeatures = Object.keys(allNavItems);

    if (all) {
      features = allFeatures;
    } else {
      features.push('help', 'copilot', 'community');
    }

    allFeatures.forEach(function (feature) {
      if (features.includes(feature) && allNavItems[feature]) {
        nav.push(allNavItems[feature]);
      }
    });

    if (features.includes('smartlocks_dashboard') && !all) {
      nav.push(allNavItems['devices']);
    }

    return nav;
  }

  public expandChange() {
    if (this.overrideExpanded) {
      this.showMobileNav();
      return;
    }

    this.navbarService.setExpanded(!this.expanded);
  }

  logout(): void {
    this.authService.logout();
  }

  showMobileNav(): void {
    setTimeout(() => {
      this.mobileMenuVisible$.next(true);
    }, 10);
  }

  clickOutside(event: MouseEvent): void {
    if (!this.mainNavigation?.nativeElement) return;
    if (!this.mainNavigation.nativeElement?.contains(event.target) && this.mobileMenuVisible$.value === true) {
      // Close the expanded nav bar
      this.hideMobileMenu();
    }
    if (!this.mainNavigation.nativeElement?.contains(event.target) && this.elementRef.nativeElement.classList.contains('absolute')) {
      if (this.expanded) {
        this.expandChange();
      }
    }
  }

  setDebugMode() {
    this.csService.toggleDebugMode();
  }

  enableDemoMode(): void {
    this.demoService.toggleDemoMode(true);
  }

  changeActiveTeam(newTeamId: number) {
    return this.userManagementService.switchActiveTeam(newTeamId).subscribe((res) => {});
  }

  public hideMobileMenu() {
    this.mobileMenuVisible$.next(false);
  }

  openCopilotModal(): void {
    this.segmentIoService.track(SegmentEvent.CopilotOpened, {});

    this.dialog.open(CopilotComponent, {
      panelClass: 'copilot-modal',
    });
  }

  private openCommunity() {
    window.open('/community', '_blank');
  }

  public countdown(value: string, showExpired = true, suffix = 'remaining') {
    return this.countdownService.createCountdown(value, showExpired, suffix).pipe(
      tap((res) => {
        if (res === 'Expired') {
          // this.logout();
        }
      })
    );
  }
}
